import { useState, useCallback } from 'react';
import { css } from '@emotion/react';
import { useAppShell } from '@/hooks/useAppShell';
import { useSideNav } from '@/hooks/useSideNav';

const navWrapper = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 96px;
  padding: 0 40px;
  background: #fff;
  box-shadow: 0px 4px 15px 0px rgba(0, 0, 0, 0.02);
  overflow: hidden;
  position: relative;
  z-index: 10;

  @media (max-width: 1169px) {
    height: 56px;
    padding: 0 24px;
  }
`;

const navWrapperShell = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 16px;
  background: transparent;
  overflow: hidden;
  position: relative;
  z-index: 10;

  @media (max-width: 1169px) {
    padding: 0 24px;
  }
`;

const logoStyles = css`
  width: 160px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 80px;

    svg {
      height: 10px;
    }
  }
`;

const navItems = css`
  display: flex;
  align-items: center;
  gap: 24px;

  @media (max-width: 1169px) {
    display: none;
  }
`;

const navDropdown = css`
  display: none;

  @media (max-width: 1169px) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 36px;
    padding: 0 8px;
    border-radius: 6px;
    background: transparent;
    border: none;
    cursor: pointer;
    font-family: 'FlodeskSans', sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: #10100f;
    white-space: nowrap;
  }
`;

const navLink = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.16px;
  color: rgba(61, 46, 41, 0.52);
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
  transition: color var(--grn-transition-fast, 200ms) ease;

  &:hover {
    color: #121212;
  }
`;

const navLinkActive = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.16px;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
  color: #121212;
`;

const divider = css`
  width: 1px;
  height: 16px;
  background: rgba(55, 49, 47, 0.12);
`;

const profileSection = css`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
  width: 160px;
  flex-shrink: 0;
  cursor: pointer;

  @media (max-width: 767px) {
    width: 80px;
  }
`;

const profileAvatar = css`
  width: 40px;
  height: 40px;
  border-radius: 60px;
  border: 1px solid rgba(55, 49, 47, 0.12);
  object-fit: cover;

  @media (max-width: 767px) {
    width: 32px;
    height: 32px;
  }
`;

const profileAvatarShell = css`
  width: 32px;
  height: 32px;
  border-radius: 60px;
  border: 1px solid rgba(55, 49, 47, 0.12);
  object-fit: cover;
`;

const chevronDown = css`
  width: 10px;
  height: 14px;
  color: rgba(61, 46, 41, 0.52);

  @media (max-width: 1169px) {
    display: none;
  }
`;

const dropdownChevron = css`
  color: var(--grn-color-icon, rgba(61, 46, 41, 0.52));
`;

const overlay = css`
  position: fixed;
  inset: 0;
  z-index: 100;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 80px;
`;

const closeButton = css`
  position: absolute;
  top: 28px;
  right: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--grn-color-icon, rgba(61, 46, 41, 0.52));
  transition: color var(--grn-transition-fast, 200ms) ease;

  &:hover {
    color: #121212;
  }
`;

const overlayNav = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const overlayLink = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 30px;
  line-height: 34.03px;
  color: rgba(61, 46, 41, 0.52);
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
  background: none;
  border: none;
  padding: 0;
  transition: color var(--grn-transition-fast, 200ms) ease;

  &:hover {
    color: #121212;
  }
`;

const overlayLinkActive = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 30px;
  line-height: 34.03px;
  color: #10100f;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
  background: none;
  border: none;
  padding: 0;
`;

const secondaryNav = css`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const iconButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 8px;
  color: var(--grn-color-icon, rgba(57, 48, 45, 0.52));
  transition:
    color var(--grn-transition-fast, 200ms) ease,
    background var(--grn-transition-fast, 200ms) ease;

  &:hover {
    color: var(--grn-color-content, #121212);
    background: var(--grn-color-overlay, rgba(58, 47, 44, 0.08));
  }
`;

const manageAndProfile = css`
  display: flex;
  align-items: center;
  gap: 24px;
  justify-content: flex-end;
  width: 160px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 80px;
  }
`;

export default function TopNav() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showDivider, setShowDivider] = useState(true);
  const [showHome, setShowHome] = useState(false);
  const [compactSecondary, setCompactSecondary] = useState(true);
  const [textSecondary, setTextSecondary] = useState(false);
  const [checkoutsIndex, setCheckoutsIndex] = useState(0);
  const checkoutsNames = ['Checkouts', 'Sales pages', 'Pages'];
  const [isShell, setIsShell] = useAppShell();
  const [, setSideNav] = useSideNav();

  const openMenu = useCallback(() => setIsMenuOpen(true), []);
  const closeMenu = useCallback(() => setIsMenuOpen(false), []);
  const toggleDivider = useCallback(() => setShowDivider(prev => !prev), []);
  const toggleShell = useCallback(() => setIsShell(prev => !prev), [setIsShell]);
  const toggleHome = useCallback(() => setShowHome(prev => !prev), []);
  const toggleSideNav = useCallback(() => setSideNav(prev => !prev), [setSideNav]);
  const toggleCompactSecondary = useCallback(() => setCompactSecondary(prev => !prev), []);
  const toggleTextSecondary = useCallback(() => setTextSecondary(prev => !prev), []);
  const cycleCheckoutsName = useCallback(
    () => setCheckoutsIndex(prev => (prev + 1) % checkoutsNames.length),
    [checkoutsNames.length],
  );

  return (
    <>
      <nav css={isShell ? navWrapperShell : navWrapper}>
        <div
          css={[
            logoStyles,
            css`
              cursor: pointer;
            `,
          ]}
          role="button"
          tabIndex={0}
          onClick={toggleShell}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') toggleShell();
          }}
          aria-label="Toggle app shell"
        >
          <svg height="14" viewBox="0 0 57 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M14.3188 3.02031C16.9384 3.02031 18.8421 4.93639 18.8421 7.51821C18.8421 10.1001 16.9384 12 14.3188 12C11.6667 12 9.81182 10.1001 9.81182 7.51821C9.81182 4.93639 11.6667 3.02031 14.3188 3.02031ZM14.3188 4.74153C12.7568 4.74153 11.748 5.92696 11.748 7.502C11.748 9.12584 12.7568 10.295 14.3188 10.295C15.8971 10.295 16.8733 9.10952 16.8733 7.502C16.8733 5.94317 15.8808 4.74153 14.3188 4.74153Z"
              fill="#111110"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M35.1292 3.02031C37.944 3.02031 39.4735 5.08255 39.4735 7.59945C39.4735 7.7131 39.4735 7.92421 39.4573 8.08661H32.6235C32.6886 9.4993 33.8601 10.3599 35.2268 10.3599C36.1542 10.3599 36.9353 9.9377 37.4234 9.12586L39.3108 9.46685C38.7739 11.0906 36.984 11.9837 35.1455 11.9837C32.5096 11.9837 30.6873 10.1326 30.6873 7.51821C30.6873 4.96891 32.5259 3.02033 35.1292 3.02031ZM35.1292 4.61166C33.795 4.61167 32.8512 5.43991 32.656 6.60902H37.4559C37.2769 5.35866 36.4471 4.61166 35.1292 4.61166Z"
              fill="#111110"
            />
            <path
              d="M44.0942 2.9878C45.9002 2.9878 47.1695 3.97828 47.2996 5.55337L45.461 5.6184C45.3146 4.98506 44.8101 4.53042 43.9966 4.53042C43.1994 4.53042 42.7275 4.9201 42.7275 5.47213C42.7275 5.92685 43.0041 6.13794 43.6875 6.3653L45.3308 6.9336C46.6975 7.40454 47.4949 7.9566 47.4949 9.25564C47.4949 11.0256 46.0467 11.9836 44.0942 11.9836C42.1254 11.9836 40.7749 11.0256 40.6448 9.33679L42.581 9.25564C42.6299 9.88892 43.1505 10.4735 44.1105 10.4735C45.0054 10.4735 45.4934 10.0513 45.4934 9.46676C45.4934 8.89844 45.1193 8.70355 44.2081 8.39508L43.0041 8.00528C41.6211 7.56688 40.775 6.90115 40.775 5.63461C40.775 4.0433 42.1091 2.9878 44.0942 2.9878Z"
              fill="#111110"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M28.9135 11.7721H27.1074V11.155C27.1074 10.9276 27.1074 10.749 27.14 10.6029H27.0749C26.489 11.4635 25.4478 11.9669 24.2112 11.9669C21.7706 11.9669 20.0947 10.0833 20.0947 7.53396C20.0947 4.98458 21.7705 3.03599 24.1298 3.03596C25.3827 3.03596 26.4241 3.57189 26.9772 4.35136H27.0261C27.0098 3.45824 26.9935 3.23089 26.9935 2.22413V0.0481693H28.9135V11.7721ZM24.5691 4.77349C23.0072 4.7735 22.0309 6.00759 22.0309 7.51766C22.0309 9.06033 23.0234 10.2781 24.5691 10.2781C26.0986 10.2781 27.1074 9.07655 27.1074 7.50145C27.1074 5.95883 26.0986 4.77349 24.5691 4.77349Z"
              fill="#111110"
            />
            <path
              d="M4.03511 0C4.21413 0 4.39312 4.0185e-05 4.60462 0.0325143V1.70501C4.53955 1.68875 4.49066 1.6888 4.40935 1.6888C3.69348 1.68881 3.22165 1.93235 3.12406 2.6143C3.10776 2.76049 3.10776 2.85791 3.10776 2.98789V3.23142H4.60462V4.85519H3.12406V11.7726H1.20405V4.85519H0V3.23142H1.20405V2.93916C1.20405 1.10429 2.03387 1.08386e-05 4.03511 0Z"
              fill="#111110"
            />
            <path d="M8.03733 11.772H6.08481V0.0481693H8.03733V11.772Z" fill="#111110" />
            <path
              d="M51.1215 2.67868C51.1215 4.10764 51.089 5.00074 51.0727 6.26729L51.089 6.2836C51.691 5.63411 52.2442 5.14693 52.8624 4.51365L54.1153 3.23087H56.7675L52.2279 7.25782L56.9139 11.772H54.1642C53.0903 10.7003 52.0489 9.57995 51.1053 8.52449H51.089C51.1053 9.23892 51.1215 9.82351 51.1215 10.6353V11.772H49.2179V0.0481693H51.1215V2.67868Z"
              fill="#111110"
            />
          </svg>
        </div>

        <div css={navItems}>
          {showHome && (
            <>
              <span css={navLink}>Home</span>
              <div css={divider} />
            </>
          )}
          <span
            css={navLinkActive}
            role="button"
            tabIndex={0}
            onClick={toggleHome}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') toggleHome();
            }}
          >
            Emails
          </span>
          <span
            css={navLink}
            role="button"
            tabIndex={0}
            onClick={toggleTextSecondary}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') toggleTextSecondary();
            }}
          >
            Forms
          </span>
          <span
            css={navLink}
            role="button"
            tabIndex={0}
            onClick={toggleDivider}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') toggleDivider();
            }}
          >
            Workflows
          </span>
          <span
            css={navLink}
            role="button"
            tabIndex={0}
            onClick={cycleCheckoutsName}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') cycleCheckoutsName();
            }}
          >
            {checkoutsNames[checkoutsIndex]}
          </span>
          {!compactSecondary && !textSecondary && (
            <>
              {showDivider && <div css={divider} />}
              <span
                css={navLink}
                role="button"
                tabIndex={0}
                onClick={toggleCompactSecondary}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') toggleCompactSecondary();
                }}
              >
                Products
              </span>
              <span css={navLink}>Audience</span>
              <span css={navLink}>Analytics</span>
            </>
          )}
        </div>

        <button css={navDropdown} type="button" onClick={openMenu}>
          Emails
          <svg css={dropdownChevron} width="10" height="18" viewBox="0 0 10 18" fill="none">
            <path
              d="M1 7L5 11L9 7"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div css={compactSecondary || textSecondary ? manageAndProfile : profileSection}>
          {textSecondary && (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 24px;
              `}
            >
              <span css={navLink}>Products</span>
              <span css={navLink}>Audience</span>
              <span css={navLink}>Analytics</span>
            </div>
          )}
          {compactSecondary && (
            <div css={secondaryNav}>
              {/* Products - Bag icon */}
              <button
                css={iconButton}
                type="button"
                onClick={toggleCompactSecondary}
                aria-label="Products"
              >
                <svg width="14" height="17" viewBox="0 0 14 17" fill="none">
                  <path
                    d="M6.99969 0C9.04146 0 10.7249 1.52995 10.9684 3.50586C12.3793 3.56918 13.5226 4.69482 13.5905 6.11914L13.9968 14.6426C14.0579 15.9258 13.0344 17 11.7497 17H2.24969C0.965036 16.9998 -0.0587167 15.9258 0.00261923 14.6426L0.410822 6.11816C0.479119 4.69493 1.62135 3.57021 3.03094 3.50586C3.27441 1.53002 4.95804 0.000165407 6.99969 0ZM4.49969 5.75C4.49969 6.16421 4.1639 6.5 3.74969 6.5C3.33563 6.49982 2.99969 6.1641 2.99969 5.75V5.01074C2.40609 5.08602 1.93922 5.57771 1.90985 6.19043L1.50067 14.7139C1.48017 15.1416 1.82156 15.4998 2.24969 15.5H11.7497C12.1779 15.5 12.5191 15.1416 12.4987 14.7139L12.0925 6.19043C12.0632 5.57682 11.5946 5.08404 10.9997 5.00977V5.75C10.9997 6.16421 10.6639 6.5 10.2497 6.5C9.83563 6.49982 9.49969 6.1641 9.49969 5.75V5H4.49969V5.75ZM6.99969 1.5C5.79032 1.50016 4.78107 2.35893 4.54949 3.5H9.44988C9.21824 2.35889 8.20916 1.5 6.99969 1.5Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              {/* Audience - People icon */}
              <button css={iconButton} type="button" aria-label="Audience">
                <svg width="21" height="14" viewBox="0 0 21 14" fill="none">
                  <path
                    d="M9.6167 13.7695C8.33252 13.7695 7.69043 13.3276 7.69043 12.4438C7.69043 11.9312 7.84424 11.4014 8.15186 10.8545C8.45947 10.3027 8.89648 9.79248 9.46289 9.32373C10.0342 8.85498 10.7153 8.47656 11.5063 8.18848C12.3022 7.89551 13.186 7.74902 14.1577 7.74902C15.1343 7.74902 16.0181 7.89551 16.8091 8.18848C17.605 8.47656 18.2861 8.85498 18.8525 9.32373C19.4238 9.79248 19.8633 10.3027 20.1709 10.8545C20.4785 11.4014 20.6323 11.9312 20.6323 12.4438C20.6323 13.3276 19.9878 13.7695 18.6987 13.7695H9.6167ZM9.45557 12.3779H18.8599C18.9429 12.3779 18.999 12.3633 19.0283 12.334C19.0625 12.3096 19.0796 12.2656 19.0796 12.2021C19.0796 11.9336 18.9697 11.626 18.75 11.2793C18.5352 10.9326 18.2178 10.5981 17.7979 10.2759C17.3779 9.94873 16.8628 9.67773 16.2524 9.46289C15.6421 9.24805 14.9438 9.14062 14.1577 9.14062C13.3716 9.14062 12.6733 9.24805 12.063 9.46289C11.4526 9.67773 10.9375 9.94873 10.5176 10.2759C10.0977 10.5981 9.77783 10.9326 9.55811 11.2793C9.34326 11.626 9.23584 11.9336 9.23584 12.2021C9.23584 12.2656 9.25293 12.3096 9.28711 12.334C9.32129 12.3633 9.37744 12.3779 9.45557 12.3779ZM14.165 6.92871C13.5645 6.92871 13.02 6.77246 12.5317 6.45996C12.0435 6.14746 11.6528 5.72754 11.3599 5.2002C11.0718 4.66797 10.9277 4.08203 10.9277 3.44238C10.9277 2.80762 11.0742 2.23145 11.3672 1.71387C11.6602 1.19141 12.0508 0.776367 12.5391 0.46875C13.0273 0.15625 13.5693 0 14.165 0C14.7607 0 15.3027 0.153809 15.791 0.461426C16.2793 0.769043 16.6675 1.18164 16.9556 1.69922C17.2485 2.2168 17.395 2.79297 17.395 3.42773C17.395 4.07227 17.2485 4.66064 16.9556 5.19287C16.6675 5.72021 16.2793 6.14258 15.791 6.45996C15.3027 6.77246 14.7607 6.92871 14.165 6.92871ZM14.165 5.54443C14.4922 5.54443 14.79 5.45166 15.0586 5.26611C15.332 5.07568 15.5493 4.82178 15.7104 4.50439C15.8765 4.18213 15.9595 3.82324 15.9595 3.42773C15.9595 3.04199 15.8789 2.69531 15.7178 2.3877C15.5566 2.08008 15.3394 1.83594 15.0659 1.65527C14.7925 1.47461 14.4922 1.38428 14.165 1.38428C13.8379 1.38428 13.5376 1.47461 13.2642 1.65527C12.9956 1.83594 12.7783 2.08252 12.6123 2.39502C12.4463 2.70752 12.3633 3.0542 12.3633 3.43506C12.3633 3.83057 12.4463 4.18945 12.6123 4.51172C12.7783 4.8291 12.9956 5.08057 13.2642 5.26611C13.5376 5.45166 13.8379 5.54443 14.165 5.54443ZM1.6333 13.7695C1.09131 13.7695 0.683594 13.6523 0.410156 13.418C0.136719 13.1836 0 12.8369 0 12.3779C0 11.7725 0.146484 11.1914 0.439453 10.6348C0.732422 10.0781 1.13525 9.58252 1.64795 9.14795C2.16553 8.71338 2.76367 8.37158 3.44238 8.12256C4.12598 7.86865 4.85596 7.7417 5.63232 7.7417C6.00342 7.7417 6.37451 7.77588 6.74561 7.84424C7.1167 7.90771 7.47803 8.00293 7.82959 8.12988C8.18115 8.25195 8.51074 8.40332 8.81836 8.58398C8.59375 8.73047 8.396 8.88916 8.2251 9.06006C8.05908 9.22607 7.91504 9.40918 7.79297 9.60938C7.59766 9.50195 7.38037 9.41162 7.14111 9.33838C6.90186 9.26025 6.65283 9.20166 6.39404 9.1626C6.14014 9.11865 5.88623 9.09668 5.63232 9.09668C5.06104 9.09668 4.52393 9.18457 4.021 9.36035C3.51807 9.53613 3.07129 9.77295 2.68066 10.0708C2.29492 10.3687 1.99219 10.7056 1.77246 11.0815C1.55762 11.4526 1.4502 11.8359 1.4502 12.2314C1.4502 12.29 1.4624 12.334 1.48682 12.3633C1.51611 12.3926 1.56494 12.4072 1.6333 12.4072H6.64307C6.61377 12.666 6.65771 12.9248 6.7749 13.1836C6.89697 13.4473 7.06543 13.6426 7.28027 13.7695H1.6333ZM5.63965 7.02393C5.12207 7.02393 4.64844 6.88721 4.21875 6.61377C3.79395 6.33545 3.45459 5.96436 3.20068 5.50049C2.94678 5.03662 2.81982 4.52148 2.81982 3.95508C2.81982 3.39844 2.94678 2.89307 3.20068 2.43896C3.45947 1.97998 3.80127 1.61377 4.22607 1.34033C4.65088 1.06689 5.12207 0.930176 5.63965 0.930176C6.15723 0.930176 6.62842 1.06689 7.05322 1.34033C7.48291 1.60889 7.82471 1.97266 8.07861 2.43164C8.33252 2.88574 8.45947 3.38867 8.45947 3.94043C8.45947 4.50684 8.33252 5.02441 8.07861 5.49316C7.82471 5.96191 7.48291 6.33545 7.05322 6.61377C6.62842 6.88721 6.15723 7.02393 5.63965 7.02393ZM5.63965 5.6543C5.90332 5.6543 6.14502 5.57861 6.36475 5.42725C6.58936 5.27588 6.76758 5.0708 6.89941 4.81201C7.03613 4.55322 7.10449 4.2627 7.10449 3.94043C7.10449 3.63281 7.03613 3.35693 6.89941 3.11279C6.76758 2.86377 6.5918 2.66602 6.37207 2.51953C6.15234 2.36816 5.9082 2.29248 5.63965 2.29248C5.37598 2.29248 5.13184 2.36816 4.90723 2.51953C4.6875 2.66602 4.51172 2.86621 4.37988 3.12012C4.24805 3.36914 4.18213 3.64746 4.18213 3.95508C4.18213 4.27246 4.24805 4.56055 4.37988 4.81934C4.5166 5.07812 4.69482 5.2832 4.91455 5.43457C5.13428 5.58105 5.37598 5.6543 5.63965 5.6543Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              {/* Analytics - Chart icon */}
              <button css={iconButton} type="button" aria-label="Analytics">
                <svg width="12" height="17" viewBox="0 0 12 17" fill="none">
                  <path
                    d="M1.5 16.25C1.5 16.6642 1.16421 17 0.749999 17C0.335787 17 8.17802e-07 16.6642 -7.1031e-07 16.25L-4.48044e-07 10.25C-4.29938e-07 9.83579 0.335786 9.5 0.75 9.5C1.16421 9.5 1.5 9.83579 1.5 10.25L1.5 16.25ZM6.75 16.25C6.75 16.6642 6.41421 17 6 17C5.58579 17 5.25 16.6642 5.25 16.25L5.25 0.75C5.25 0.335786 5.58579 -2.79231e-07 6 -2.62268e-07C6.41421 -2.44162e-07 6.75 0.335786 6.75 0.75L6.75 16.25ZM12 16.25C12 16.6642 11.6642 17 11.25 17C10.8358 17 10.5 16.6642 10.5 16.25L10.5 4.25293C10.5 3.83872 10.8358 3.50293 11.25 3.50293C11.6642 3.50293 12 3.83872 12 4.25293L12 16.25Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
          )}
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              cursor: pointer;
            `}
            role="button"
            tabIndex={0}
            onClick={toggleSideNav}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') toggleSideNav();
            }}
          >
            <img
              css={isShell ? profileAvatarShell : profileAvatar}
              src="/images/profile.png"
              alt="Profile"
            />
            <svg css={chevronDown} viewBox="0 0 10 14" fill="none">
              <path
                d="M1 5L5 9L9 5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </nav>

      {isMenuOpen && (
        <div css={overlay} onClick={closeMenu} role="presentation">
          <button css={closeButton} type="button" onClick={closeMenu} aria-label="Close menu">
            <svg width="19" height="19" viewBox="0 0 19 19" fill="none">
              <path
                d="M1 1L18 18M18 1L1 18"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <div css={overlayNav}>
            <span css={overlayLink}>Home</span>
            <span css={overlayLinkActive}>Emails</span>
            <span css={overlayLink}>Forms</span>
            <span css={overlayLink}>Workflows</span>
            <span css={overlayLink}>Checkouts</span>
            <span css={overlayLink}>Products</span>
            <span css={overlayLink}>Audience</span>
            <span css={overlayLink}>Analytics</span>
          </div>
        </div>
      )}
    </>
  );
}
